// (웹) http://ringleplus.com/ko/portal/materials/list/[categoryId] 에서 쓰임
// (앱) http://local.ringleplus.com:3000/ko/webview/materials > 카테고리 선택 에서 쓰임
// ... 에서 쓰임

import { useToast } from '@repo/design-system/index'
import { QIcon_chevron_left } from '@repo/icons/index'
import classNames from 'classnames'
import { useContext, useEffect, useState } from 'react'
import { useEventTracking } from '../../../../helpers/gtmHelper'
import { useBookMarkedMaterials, useMaterialsByCategoryId } from '../../../../modules/apis/student/material/queries'
import { APP_SCREEN_NAME, STACK_ACTIVITY_NAME } from '../../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import { useOnclickHeartPacket } from '../../../../modules/hooks/common/useOnclickHeartPacket'
import useReturnFullUrl from '../../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_ai_speaking, n_material, s_my_original } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import urls from '../../../../modules/urls'
import { COLORS } from '../../../../modules/vars'
import { SelectMaterialContext } from '../../../../pages/[lang]/portal/lessons/materials/[lessonId]'
import { useAITutorStack } from '../../../../pages/[lang]/webview/ai-tutor'
import { useConversationAIStack } from '../../../../pages/[lang]/webview/conversational-ai'
import { useLessonsStack } from '../../../../pages/[lang]/webview/lessons'
import { useMaterialsStack } from '../../../../pages/[lang]/webview/materials'
import { ConversationCourseSelectInstruction } from './RecommendedListSection'
import { BOOKMARKED_MATERIALS } from './SelectMaterialBody'
import SelectMaterialCard, { SelectMaterialCardSkeleton } from './SelectMaterialCard'

interface ICategoryListSection {
  categoryId: number
  setCategoryId: (categoryId: number) => void
  selectedHashId: string
  setSelectedHashId: (hashId: string) => void
  isList?: boolean
  isConversation?: boolean | { [x: string]: boolean }
  isAITutor?: boolean | { [x: string]: boolean }
}

const getSkeletonArray = (isList) => (isList ? [0, 1, 2, 3] : [0, 1, 2])

const CategoryListSection = ({
  categoryId,
  setCategoryId,
  selectedHashId,
  setSelectedHashId,
  isList = false,
  isConversation = false,
  isAITutor = false,
}: ICategoryListSection) => {
  const isMobile = useIsMobile(null, 614)
  const router = useAppRouter()
  const { t, isLocaleKO } = useTranslation()
  const { sendClickEvent } = useEventTracking()
  const { toastSuccess } = useToast()
  const { selectedCourse, setSelectedCourse, lessonId } = useContext(SelectMaterialContext)
  const lessonSelectLessonId = lessonId ? lessonId : router.query.lessonId
  const isBookmarked = categoryId == BOOKMARKED_MATERIALS
  const bookMarkedMaterials = useBookMarkedMaterials()
  const materialsByCategoryId = useMaterialsByCategoryId(isBookmarked ? 0 : categoryId)

  const { data: categoryMaterialData, isFetching: categoryMaterialsIsFetching } = isBookmarked
    ? bookMarkedMaterials
    : materialsByCategoryId

  const [materialData, setMaterialData] = useState({ small_hashtags: [] })
  const [materialList, setMaterialList] = useState([])

  const { useLessonsFlow } = useLessonsStack()
  const { push: lessonPush } = useLessonsFlow()
  const lessonRouter = useAppRouter({ stackPush: lessonPush })

  const { useMaterialsFlow } = useMaterialsStack()
  const { push: materialsPush } = useMaterialsFlow()
  const materialsRouter = useAppRouter({ stackPush: materialsPush })

  const { useConversationAIFlow } = useConversationAIStack()
  const { push: conversationPush } = useConversationAIFlow()
  const conversationRouter = useAppRouter({ stackPush: conversationPush })

  const { useAITutorFlow } = useAITutorStack()
  const { push: useAITutorPush } = useAITutorFlow()
  const aiTutorRouter = useAppRouter({ stackPush: useAITutorPush, browserDetect: true })
  const getFullUrl = useReturnFullUrl()

  const isCategory = [12, 13, 14, 15, 16, 17, 18, 19, 21, BOOKMARKED_MATERIALS].includes(categoryId)
  const isMadedByMySelfMaterial = categoryId == 19

  const titleTranslations = (data) =>
    isBookmarked
      ? t(s_my_original.saved_packets)
      : [data.title_kr, data.title_en, '', data.title_zh_chs, data.title_zh_cht, data.title_ja, data.title_vi]

  useEffect(() => {
    if (categoryMaterialData && !categoryMaterialsIsFetching) {
      setMaterialData(categoryMaterialData)
      const filteredCourseList =
        selectedHashId && categoryId != 21
          ? categoryMaterialData?.small_hashtags
              ?.filter((hash) => hash.title_en == selectedHashId)
              .flatMap((hash) => hash.courses) || []
          : categoryMaterialData?.small_hashtags[0]?.courses
      setMaterialList(filteredCourseList)
    }
  }, [categoryMaterialData, categoryMaterialsIsFetching, selectedHashId])

  const handleListButton = () => {
    setCategoryId(0)
    const newUrl = new URL(window.location.href)
    newUrl.searchParams.set('category', '')
    window.history.pushState({}, '', newUrl)
  }

  const onClickHeartPacket = useOnclickHeartPacket({
    packetItems: materialList,
    setPacketItems: setMaterialList,
    isList: true,
    onAdded: () => toastSuccess(t(n_material.toast_book_mark)),
    onDeleted: () => toastSuccess(t(n_material.toast_delete_book_mark)),
  })

  const handleClickSelect = (course) => {
    const isSelected = course.id === selectedCourse?.id

    if (isSelected) {
      setSelectedCourse(undefined)
    } else {
      setSelectedCourse(course)
    }
  }

  const onClickCourseDetail = (course) => {
    if (isConversation) {
      conversationRouter.push('', {
        stackActivity: STACK_ACTIVITY_NAME.CONVERSATION_MATERIAL_DETAIL_ACTIVITY,
        stackSendData: {
          materialId: course.id,
        },
      })
      return
    }

    if (isAITutor) {
      aiTutorRouter.push(urls.webview.aiTutor.discussion.materialDetail(course.id), {
        appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
        appSendData: {
          url: getFullUrl(urls.webview.aiTutor.discussion.materialDetail(course.id)),
        },
      })
      return
    }

    router.asPath.includes('webview/materials')
      ? materialsRouter.push(
          isList
            ? urls.student.newPortal.materials.detail(course.id)
            : urls.student.newPortal.materials.detail(course.id) + `?lessonId=${lessonSelectLessonId}`,
          {
            stackActivity: STACK_ACTIVITY_NAME.MATERIAL_DETAIL_ACTIVITY,
            stackSendData: {
              materialId: course.id,
              lessonId: lessonSelectLessonId,
            },
          }
        )
      : lessonRouter.push(
          isList
            ? urls.student.newPortal.materials.detail(course.id)
            : urls.student.newPortal.materials.detail(course.id) + `?lessonId=${lessonSelectLessonId}`,
          {
            appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
            appSendData: {
              url: getFullUrl(urls.webview.lessons.lessonMaterialDetail(lessonSelectLessonId, course.id)),
            },
          }
        )
  }

  useEffect(() => {
    if (categoryMaterialData?.small_hashtags) {
      setSelectedHashId(categoryMaterialData?.small_hashtags[0].title_en)
    }
  }, [categoryMaterialData])

  return (
    <div className={classNames(isList ? 'mt-[40px] lg:ml-[0px]' : 'lg:ml-[196px]', 'flex flex-col')}>
      {/* 추천 목록으로 이동하기 */}
      {!isCategory && !isList && (
        <div className="mb-[16px] flex">
          <button className="flex flex-row items-center gap-[2px]" onClick={handleListButton}>
            <QIcon_chevron_left width={16} height={16} color={COLORS.GRAY500} className="mb-[2px]" />
            <div className="Body2-14Bd text-gray-600">추천 목록</div>
          </button>
        </div>
      )}

      {isMadedByMySelfMaterial && (
        <div className="flex flex-row rounded-[4px] p-[12px] gap-[4px] bg-gray-100 mb-[48px]">
          <img
            className="w-[24px] h-[24px]"
            src={'https://d38emex6h5e12i.cloudfront.net/2024-image/1714457519353_ai_speaking_partner_default.webp'}
            alt="ai_tutor"
          />
          <div className="text-gray-700 Body2-14Bd">{t(n_ai_speaking.material_msg)}</div>
        </div>
      )}
      {/* 카테고리 교재 목록 */}
      {materialData && !categoryMaterialsIsFetching ? (
        <>
          <div className="Headline2-24Bd">
            {t(titleTranslations(categoryMaterialData))} ({categoryMaterialData.small_hashtags[0]?.courses.length})
          </div>

          <div className="mt-[16px] flex flex-wrap gap-[8px] border-gray-300 font-medium">
            {categoryMaterialData.small_hashtags.map((hash, i) => {
              return (
                <button
                  key={i}
                  onClick={() => setSelectedHashId(hash.title_en)}
                  className={classNames(
                    selectedHashId == hash.title_en &&
                      'border-purple-500 bg-purple-50 text-[16px] font-bold text-purple-500',
                    'rounded-[8px] border-[1.5px] px-[16px] py-[8px] text-[16px] '
                  )}
                >
                  {isLocaleKO ? hash.title_kr : hash.title_en}
                </button>
              )
            })}
          </div>
          {isConversation && (
            <div className="mt-[20px]">
              <ConversationCourseSelectInstruction />
            </div>
          )}
          <div
            className={classNames(
              isList ? 'md:grid-cols-4' : 'md:grid-cols-3',
              'mt-[36px] grid grid-cols-1 gap-y-[10px] gap-x-[12px] sm:grid-cols-2 sm:gap-y-[24px]'
            )}
          >
            {materialList?.map((course) => {
              return (
                <div
                  className=""
                  key={course.id}
                  onClick={() => {
                    sendClickEvent({ button_name: 'material_card', object_id: course.id })
                    onClickCourseDetail(course)
                  }}
                >
                  <SelectMaterialCard
                    horizontal={isMobile}
                    title={course.title}
                    subtitle={course.subtitle}
                    is_mine={course.is_mine}
                    category={course.category}
                    image_url={course.image_url}
                    is_revised={course.is_revised}
                    is_today={course.is_today}
                    selected={course.id === selectedCourse?.id}
                    onClickSelect={() => handleClickSelect(course)}
                    onClickHeart={(e) => onClickHeartPacket(e, course)}
                    isList={isList}
                    level={course.level}
                    audioLength={course.mp3_running_time_in_minute}
                  />
                </div>
              )
            })}
          </div>
        </>
      ) : (
        <>
          <div className="border-1 h-[34px] w-[240px] animate-pulse rounded-[4px] bg-gray-100"></div>
          <div className=" mt-[16px] flex animate-pulse flex-wrap gap-[8px] border-gray-300 font-medium">
            <div className="flex flex-row gap-[16px]">
              <button
                className={'border-1 h-[42px] w-[64px] rounded-[8px] bg-gray-100 px-[16px] py-[8px] text-[16px]'}
              ></button>
              <button
                className={'border-1 h-[42px] w-[64px] rounded-[8px] bg-gray-100 px-[16px] py-[8px] text-[16px]'}
              ></button>
              <button
                className={'border-1 h-[42px] w-[64px] rounded-[8px] bg-gray-100 px-[16px] py-[8px] text-[16px]'}
              ></button>
            </div>
          </div>
          <div className="w-full animate-pulse">
            {getSkeletonArray(isList).map((item, i) => {
              return (
                <div className="mb-[32px] mt-[36px] w-full" key={i}>
                  <section className="">
                    <div
                      className={classNames(
                        isList ? 'md:grid-cols-4' : 'md:grid-cols-3 lg:w-[688px]',
                        'flex w-full flex-row gap-[9px] sm:grid sm:grid-cols-2 '
                      )}
                    >
                      {[0, 1, 2].map((_, i) => (
                        <div className="w-full" key={i}>
                          <SelectMaterialCardSkeleton />
                        </div>
                      ))}
                    </div>
                  </section>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

export default CategoryListSection
